import { array, option, record } from 'fp-ts';
import { pipe } from 'fp-ts/function';

import { CategoryAvailability, Restriction, ServiceAvailabilityResult } from '@mews-distributor/data-access';
import { values } from '@mews/fp';
import { DateTimeService, DateTimeUtc, DateTimeUtcService } from '@mews/framework';
import { API_MAXIMUM_INTERVAL_TIME_UNITS } from '../../constants';
import { ServiceAvailabilityData } from './types';
import { MakeUtcDatesParams } from './utils';

interface GetHourlyDateIntervals {
    startUtc: DateTimeUtc;
    endUtc: DateTimeUtc;
}

// We will only ever fetch the visible range due to API limitations.
export const getHourlyDateIntervals = ({ startUtc, endUtc }: GetHourlyDateIntervals) => {
    const hoursStartToEnd = DateTimeUtcService.diffHours(DateTimeUtcService.endOf(endUtc, 'month'), startUtc);
    return pipe(
        array.makeBy(hoursStartToEnd, (i) => DateTimeUtcService.addHours(startUtc, i)),
        array.chunksOf(API_MAXIMUM_INTERVAL_TIME_UNITS),
        array.map((chunk) => ({ start: chunk[0], end: chunk[chunk.length - 1] }))
    );
};

export const getCombinedAvailability = (availability: CategoryAvailability[][]) =>
    pipe(
        availability,
        array.flatten,
        array.reduce({}, (acc: Record<string, number[]>, { categoryId, availabilities }) => {
            // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
            if (!acc[categoryId]) acc[categoryId] = availabilities;
            else acc[categoryId] = [...acc[categoryId], ...availabilities];
            return acc;
        }),
        record.toEntries,
        array.map(([categoryId, availabilities]) => ({ categoryId, availabilities }))
    );

export const getCombinedRestrictions = (restrictions: Restriction[][]) =>
    pipe(restrictions, array.flatten, array.uniq({ equals: (a, b) => a.id === b.id }));

export const getCombinedResponse = (response: ServiceAvailabilityResult[]) => {
    const combinedTimeUnits = pipe(
        response,
        array.map(({ timeUnitStartsUtc }) => timeUnitStartsUtc),
        array.flatten
    );

    const combinedAvailabilities = pipe(
        response,
        array.map(({ categoryAvailabilities }) => categoryAvailabilities),
        getCombinedAvailability
    );

    const combinedRestrictions = pipe(
        response.map(({ applicableRestrictions }) => applicableRestrictions),
        getCombinedRestrictions
    );

    const combinedCategoryMappings = pipe(
        response.map(({ categoryMappings }) => option.fromNullable(categoryMappings)),
        array.compact,
        array.flatten,
        array.uniq({ equals: (a, b) => a.resourceCategoryId === b.resourceCategoryId })
    );

    return {
        timeUnitStartsUtc: combinedTimeUnits,
        categoryAvailabilities: combinedAvailabilities,
        applicableRestrictions: combinedRestrictions,
        categoryMappings: combinedCategoryMappings,
    };
};

export const getEarliestDateTime = (dateTimes?: DateTimeUtc[]) =>
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    (dateTimes?.length || 0) > 0
        ? dateTimes?.reduce((earliest, current) => DateTimeUtcService.min(earliest, current))
        : null;

interface GetFirstAvailableDateTime {
    data: ServiceAvailabilityData;
    timezone: string;
    startUtc: DateTimeUtc;
}

export const getFirstAvailableDateTime = ({ data, timezone, startUtc }: GetFirstAvailableDateTime) => {
    if (timezone == null) {
        return null;
    }
    return pipe(
        values(data),
        array.map(({ timeUnitsAvailability }) =>
            pipe(
                record.toArray(timeUnitsAvailability),
                array.findFirst(
                    ([dateTimeUtc, count]) =>
                        count > 0 &&
                        DateTimeUtcService.isGreaterOrEqual(DateTimeUtcService.parse(dateTimeUtc), startUtc)
                ),
                option.map(([dateTimeUtc]) => DateTimeUtcService.parse(dateTimeUtc))
            )
        ),
        array.compact,
        getEarliestDateTime
    );
};

/**
 * @returns array of dateTimeUtc ISO strings without milliseconds by hour
 */
export const makeHourlyUtcDates = ({
    startUtc,
    endUtc,
    includeEndUtc = false,
    timezone,
}: MakeUtcDatesParams): string[] => {
    const startDateTime = DateTimeService.fromUtc(startUtc, timezone);
    const endDateTime = DateTimeService.fromUtc(endUtc, timezone);
    const createDate = (hoursFromStart: number): string =>
        pipe(
            DateTimeService.addHours(startDateTime, hoursFromStart),
            (date) => DateTimeService.toUtc(date),
            (dateTimeUtc) => DateTimeUtcService.toISOString(dateTimeUtc, { suppressMilliseconds: true })
        );
    /**
     * When using this function to find missing availability date ranges, it is important to include the last day.
     * In instances where we are using it to adjust availability, we do not want to include the last day.
     */
    const range = DateTimeService.diffHours(endDateTime, startDateTime) + (includeEndUtc ? 1 : 0);

    return array.makeBy(range, createDate);
};
